import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { AuthShell, GoogleButton, inputCls } from "@/components/AuthShell";
import { formatApiErrorDetail, useAuth } from "@/context/AuthContext";
import { useSEO } from "@/components/SEO";

export default function LoginPage() {
  useSEO({ title: "Sign In — Loan Salaah", description: "Sign in to your Loan Salaah account to track your loan and credit card applications." });
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(params.get("error") === "google" ? "Google sign-in didn't complete. Please try again." : "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/account", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(form.email, form.password);
      navigate("/account", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Sign in to track your applications."
      subtitle="Every loan and credit card application you've made with Loan Salaah, with its live status — in one calm place."
      footer={<>New to Loan Salaah? <Link to="/signup" data-testid="login-signup-link" className="font-semibold text-concordia underline decoration-brass/50 underline-offset-2">Create an account</Link></>}
    >
      <form data-testid="login-form" onSubmit={submit} className="mt-8 space-y-5" noValidate>
        <div>
          <label htmlFor="login-email-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Email</label>
          <input id="login-email-input" data-testid="login-email-input" type="email" className={inputCls} placeholder="you@example.com"
            value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label htmlFor="login-password-input" className="block text-xs font-semibold uppercase tracking-wider text-slate">Password</label>
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-medium text-concordia underline decoration-brass/50 underline-offset-2">Forgot?</Link>
          </div>
          <input id="login-password-input" data-testid="login-password-input" type="password" className={inputCls} placeholder="Your password"
            value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
        </div>
        {error && <p data-testid="login-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">{error}</p>}
        <button type="submit" data-testid="login-submit-button" disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-concordia px-8 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-concordia-deep disabled:cursor-not-allowed disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <div className="flex items-center gap-4 py-1">
          <span className="h-px flex-1 bg-sand" />
          <span className="text-[0.65rem] uppercase tracking-[0.25em] text-slate">or</span>
          <span className="h-px flex-1 bg-sand" />
        </div>
        <GoogleButton />
      </form>
    </AuthShell>
  );
}
